import { addDays, differenceInDays, endOfDay, isBefore } from 'date-fns';
import { AssignmentCDCUpToDateStatusEnum } from '../../enums/assignment-cdc-up-to-date.enum';
import { AssignmentContract } from './assignmentContract.model';
import { AssignmentPolicies } from './assignmentPolicies.model';
import { CheckinState } from '../../enums/checkin-state.enum';
import { CheckoutState } from '~app/shared/enums/checkout-state.enum';
import { DocumentStatus } from '../../enums/documentStatus.enum';
import { DocusignStatus } from '../../enums/docusignStatus.enum';
import { HealthCheck } from '../healthCheck/healthCheck.model';
import { HealthCheckState } from '../../enums/healthCheckState.enum';
import { IAssignmentData } from '../../interfaces/assignment-data.interface';
import { ICovidVaxInfo } from '../../interfaces/covid-vax-info.interface';
import { LastOnBoardStatesEnum } from '../../enums/last-onboard-states.enum';
import { parseDateFromMW } from '~app/shared/functions/utils/utils.function';
import { Port } from '../port.model';
import { Ship } from '../ship.model';
import { SOEA_EXEMPT_JOB_CODES } from '../../constants/jobCodes.constant';
import { VacStatus } from '../vaxStatus.enum';
import { WorkflowState } from '../workflow';

export class Assignment {
  public readonly ship: Ship;
  public readonly signOnPort?: Port;
  public readonly signOffPort?: Port;
  public policies: AssignmentPolicies;
  public soea: AssignmentContract;
  public healthCheck: HealthCheck;

  public readonly data: IAssignmentData;
  private _workflowState: WorkflowState;

  constructor(data: any, ships: Array<Ship> | Ship, ports?: Array<Port> | Port) {
    this.data = data;
    this.ship = Array.isArray(ships) ? ships.find(ship => ship.code === this.data.shipCode) : ships;
    if (!this.ship) {
      this.ship = new Ship({
        activeStatus: 'TRUE',
        brand: '0',
        code: this.data.shipCode,
        name: 'Unknown',
        shipClass: this.data.shipCode,
      });
    }

    if (ports) {
      this.signOnPort = Array.isArray(ports) ? ports.find(port => port.code === this.data.signonPort) : ports;
      this.signOffPort = Array.isArray(ports) ? ports.find(port => port.code === this.data.signoffPort) : ports;
    }
    if (!this.signOnPort) {
      this.signOnPort = new Port({
        airport: '',
        code: this.data.signonPort,
        country: '',
        name: 'Unknown',
        timezoneOffsetEST: '0',
        type: 'T',
      });
    }
    if (!this.signOffPort) {
      this.signOffPort = new Port({
        airport: '',
        code: this.data.signoffPort,
        country: '',
        name: 'Unknown',
        timezoneOffsetEST: '0',
        type: 'T',
      });
    }

    this.data.docusignViews = {};
    this.data.docType = 'SOEA';
  }

  public get checkInState(): CheckinState {
    const shipCheckInEnabled = this.ship.checkInEnabled;
    const checkInEnabled = this.checkInEnabled;
    const precheckStatus = this.workflowState.preCheckStatus;

    if (!shipCheckInEnabled) {
      return CheckinState.DISABLED;
    }

    if (!checkInEnabled) {
      return CheckinState.CLOSED;
    }

    if (precheckStatus === 0) {
      return CheckinState.OPEN;
    }

    if (precheckStatus > 0 && precheckStatus < 100) {
      return CheckinState.PENDING;
    }

    if (precheckStatus === 100) {
      return this.healthCheckStatus();
    }

    console.error('There was a problem calculating check-in status');
    return CheckinState.CLOSED;
  }

  private healthCheckStatus(): CheckinState {
    const isMedicalEnabled = this.ship.medicalEnabled;
    const healthPercentage = this.workflowState.data.healthCheckStatus;
    if (healthPercentage === 1 || healthPercentage === 2) {
      return isMedicalEnabled ? CheckinState.HEALTH_CHECK_COMPLETED : CheckinState.COMPLETED;
    }
    if (this.healthCheckEnabled) {
      return isMedicalEnabled ? CheckinState.HEALTH_CHECK_OPEN : CheckinState.COMPLETED;
    }
    return CheckinState.COMPLETED;
  }

  public get checkoutState(): CheckoutState {
    if (!this.ship.checkOutEnabled) return CheckoutState.DISABLED;
    if (!this.checkoutEnabled) return CheckoutState.CLOSED;

    const status = this.workflowState?.debarkStatus;
    if (status === 0) return CheckoutState.OPEN;
    if (status > 0 && status < 100) return CheckoutState.PENDING;
    if (status === 100) return CheckoutState.COMPLETED;
    console.error('There was a problem calculating check-out status');
    return CheckoutState.CLOSED;
  }

  public get checkInEnabled(): boolean {
    return this.data.checkinEnabled === 'TRUE';
  }

  public get checkoutEnabled(): boolean {
    return this.data.checkoutEnabled === 'TRUE';
  }

  public get healthCheckEnabled(): boolean {
    return this.data.healthCheckEnabled === 'TRUE';
  }

  public get envelopeId(): string {
    return this.data.envelopeId;
  }

  public get signOnDate(): Date {
    if (!this.data.signonDate) return;
    return parseDateFromMW(this.data.signonDate);
  }

  public get signOffDate(): Date {
    return parseDateFromMW(this.data.signoffDate);
  }

  public get isCheckInCompleted(): boolean {
    return this.workflowState.data.precheckStatus === 100;
  }

  public get isCheckOutCompleted(): boolean {
    return this.workflowState.data.debarkStatus === 100;
  }

  public get isHealthCheckCompleted(): boolean {
    return this.workflowState.data.healthCheckStatus !== HealthCheckState.Pending;
  }

  public get healthCheckState(): HealthCheckState {
    return this.workflowState.data.healthCheckStatus;
  }

  public get medicalExpDate(): Date {
    return this.data.medicalExpDate && this.data.medicalExpDate !== '0'
      ? parseDateFromMW(this.data.medicalExpDate)
      : undefined;
  }

  public get pcrIssueDate(): Date {
    return this.data.pcrIssueDate && this.data.pcrIssueDate !== '0'
      ? parseDateFromMW(this.data.pcrIssueDate)
      : undefined;
  }

  public get isDocuSignAvailable(): boolean {
    if (
      this.data.docusignStatus &&
      this.data.docusignStatus.envelopeStatus &&
      this.data.envelopeId &&
      this.data.serviceAgreementLink
    ) {
      return true;
    }
    return false;
  }

  public get isDocumentApproved(): boolean {
    return this.workflowState.data.immigrationStatus === 100;
  }

  public get isMedicalApproved(): boolean {
    return this.workflowState.data.medicalStatus === 100;
  }

  public get isCertificateApproved(): boolean {
    return this.workflowState.data.safetyStatus === 100;
  }

  public get isContractExpired(): boolean {
    const today = new Date();
    return today > endOfDay(this.signOnDate);
  }

  public get isExpiredForAdmin(): boolean {
    if (!this.signOnDate) return false;
    const today = new Date();
    return today > endOfDay(addDays(this.signOnDate, 3));
  }

  public get isContractor(): boolean {
    return this.data.contractor === 'TRUE';
  }

  public get contractReadyToRelease(): boolean {
    if (!this.isCheckInCompleted) return false;
    if (!this.isDocumentApproved) return false;
    if (!this.managerSigned) return false;
    if (!this.crewMemberSigned) return false;
    if (this.ship.medicalEnabled && !this.isMedicalApproved) return false;
    if (this.ship.safetyEnabled && !this.isCertificateApproved) return false;
    return true;
  }

  public get contractReleased(): boolean {
    return (
      this.data?.docusignStatus?.envelopeStatus &&
      this.data?.docusignStatus?.envelopeStatus?.toLowerCase() === DocusignStatus.Completed
    );
  }

  public get crewMemberSigned(): boolean {
    return (
      this.data.docusignStatus.crewMemberRecipientStatus &&
      this.data.docusignStatus.crewMemberRecipientStatus.toLowerCase() === DocusignStatus.Completed
    );
  }

  public get managerSigned(): boolean {
    return (
      this.data.docusignStatus.hrManagerRecipientStatus &&
      this.data.docusignStatus.hrManagerRecipientStatus.toLowerCase() === DocusignStatus.Completed
    );
  }

  public get isContractCorrupted(): boolean {
    return isBefore(this.signOnDate, new Date('2019-06-16T23:59:00.000Z'));
  }

  public get contractAvailable(): boolean {
    if (!this.data.envelopeId || !this.data.docusignStatus || !this.data.docusignStatus.envelopeStatus) {
      return false;
    }
    return true;
  }

  public get isContractNA(): boolean {
    return this.isContractor || SOEA_EXEMPT_JOB_CODES.includes(this.posCode);
  }

  public get contractStatus(): DocumentStatus {
    if (this.isContractNA) return DocumentStatus.NotApplicable;
    if (!this.contractAvailable) return DocumentStatus.Missing;
    if (!this.contractReleased) return DocumentStatus.Pending;
    return DocumentStatus.Completed;
  }

  public get fullName(): string {
    if (this.data.firstName && this.data.lastName) {
      return `${this.data.lastName}, ${this.data.firstName}`;
    } else {
      if (this.data.firstName) return this.data.firstName;
      if (this.data.lastName) return this.data.lastName;
      return 'Missing name';
    }
  }

  public get crewMemberId(): string {
    return this.data.crewMemberId;
  }

  public get posCode(): string {
    return this.data.positionJobCode;
  }

  public get documentType(): string {
    return this.data.docType;
  }

  public get isMedicalCertificateExpired(): boolean {
    return this.medicalExpDate < this.signOnDate;
  }

  public get isMedicalCertificateExpiredDuringSignOn(): boolean {
    return this.medicalExpDate >= this.signOnDate && differenceInDays(this.medicalExpDate, this.signOnDate) <= 60;
  }

  public get cdcStatus(): AssignmentCDCUpToDateStatusEnum {
    return this.data.cdc;
  }

  public get vacStatus(): VacStatus | undefined {
    return this.data.vacStatus ?? undefined;
  }

  public get lastDoseDate(): string | undefined {
    return !!this.data.doses ? this.data.doses[this.data.doses.length - 1]?.date : undefined;
  }

  public get doses(): ICovidVaxInfo[] {
    return this.data.doses;
  }

  public get lastOnboard(): LastOnBoardStatesEnum {
    return this.data.beenOnBoardLastYear;
  }

  public get workflowState(): WorkflowState {
    return this._workflowState;
  }

  public set workflowState(workflowData: any) {
    this._workflowState = new WorkflowState({ ...this._workflowState?.data, ...workflowData });
  }
}
